import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Removes leading and trailing punctuation from a word.
 * This is used for display purposes and dictionary lookups.
 * Preserves internal punctuation like apostrophes in contractions (e.g., "hi'n").
 * Preserves Unicode letters (Welsh accents: â, ê, î, ô, û, ŵ, ŷ, etc.)
 * 
 * @param word - The word to clean
 * @returns The word without leading/trailing punctuation
 */
export function stripPunctuation(word: string): string {
  if (!word) return '';
  // Use Unicode property escapes to preserve accented letters
  // \p{L} matches any Unicode letter (including â, ŵ, ŷ, etc.)
  // \p{N} matches any Unicode number
  return word.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').trim();
}

/**
 * Removes all punctuation from a word, including internal punctuation.
 * This is more aggressive than stripPunctuation and should be used
 * when you need the base word form.
 * 
 * @param word - The word to clean
 * @returns The word without any punctuation
 */
export function removeAllPunctuation(word: string): string {
  if (!word) return '';
  return word.replace(/[.,!?;:"""'''()[\]{}\u2014–-]/g, '').trim();
}

/** Converts underscore-separated compound lemma keys to space-separated display form (e.g. "pedair_deg" → "pedair deg"). Display only — do not use for stored values. */
export function displayWelsh(word: string | null | undefined): string {
  if (!word) return '';
  return word.replace(/_/g, ' ');
}

/** A bracketed gloss ("[linking particle]") is a stored marker meaning "no
 *  English translation — the bracket names this particle's grammatical role
 *  instead", not punctuation an admin should see. Strip it for display only;
 *  the stored english_definitions.word keeps its brackets. */
export function displayGloss(translation: string | null | undefined): string | null | undefined {
  return translation?.replace(/^\[(.+)\]$/, '$1');
}

/** Pulls the backend's `{ error: "..." }` message out of an Axios error, falling back when the shape doesn't match (network error, unrelated throw). */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: { error?: string } } } | undefined)?.response;
  return response?.data?.error ?? fallback;
}
